// ============================================================================
// BOQ — bill versioning (milestones + revisions, snapshot / restore / compare)
// ============================================================================
// Two tiers: milestones (Estimate / Tender / …) each holding revisions. The
// heavy lifting (atomic snapshot + restore of lines, measurements and links)
// is done by the boq.snapshot_bill_version / boq.restore_bill_version SQL
// functions; this hook orchestrates them and loads version detail for compare.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

export const MILESTONE_KINDS = [
  { value: 'estimate', label: 'Estimate' },
  { value: 'tender', label: 'Tender' },
  { value: 'construction', label: 'Construction' },
  { value: 'final', label: 'Final account' },
  { value: 'other', label: 'Other' },
];

// Short / long stage label per milestone kind. Mirrors the spec_depth backfill
// (estimate->SD, tender->TD, construction/final->GFC) so the UI can show a
// stage chip without a taxonomy lookup.
export const STAGE_BY_KIND = {
  estimate: { short: 'SD', long: 'Schematic Design' },
  tender: { short: 'TD', long: 'Tender' },
  construction: { short: 'GFC', long: 'Construction / GFC' },
  final: { short: 'FA', long: 'Final account' },
  other: { short: '', long: 'Other' },
};

const num = (v) => (v == null ? null : Number(v));

export const useBoqBillVersions = (boqProjectId) => {
  const [milestones, setMilestones] = useState([]);
  const [versions, setVersions] = useState([]);
  const [activeMilestoneId, setActiveMilestoneId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!boqProjectId) { setMilestones([]); setVersions([]); setActiveMilestoneId(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [ms, vs, pr] = await Promise.all([
        supabase.schema('boq').from('bill_milestones')
          .select('id, name, kind, seq, note, created_at')
          .eq('project_id', boqProjectId).order('seq'),
        supabase.schema('boq').from('bill_versions')
          .select('id, milestone_id, revision_seq, code, label, is_auto, snapshot_at')
          .eq('project_id', boqProjectId).order('snapshot_at', { ascending: false }),
        supabase.schema('boq').from('projects')
          .select('active_milestone_id').eq('id', boqProjectId).maybeSingle(),
      ]);
      if (ms.error) throw ms.error;
      if (vs.error) throw vs.error;
      if (pr.error) throw pr.error;
      setMilestones(ms.data || []);
      setVersions(vs.data || []);
      setActiveMilestoneId(pr.data?.active_milestone_id || null);
    } catch (err) {
      console.error('[useBoqBillVersions] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'versions' }));
    } finally {
      setLoading(false);
    }
  }, [boqProjectId]);

  const latestMilestone = milestones.length
    ? milestones.reduce((a, b) => (b.seq > a.seq ? b : a))
    : null;

  // The stage the live bill currently belongs to (falls back to highest-seq for
  // legacy projects whose active pointer was never set).
  const activeMilestone = (activeMilestoneId && milestones.find((m) => m.id === activeMilestoneId)) || latestMilestone;

  const createMilestone = useCallback(async ({ name, kind }) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    try {
      const nextSeq = (milestones.reduce((m, x) => Math.max(m, x.seq), 0)) + 1;
      const { data, error: insErr } = await supabase.schema('boq').from('bill_milestones')
        .insert({ project_id: boqProjectId, name: name.trim(), kind, seq: nextSeq })
        .select('id, name, kind, seq, note, created_at').single();
      if (insErr) throw insErr;
      // Creating a milestone snapshots the current live bill as its first
      // revision — i.e. you continue working under this new stage, so it
      // becomes the active stage.
      await supabase.schema('boq').from('projects').update({ active_milestone_id: data.id }).eq('id', boqProjectId);
      setMilestones((prev) => [...prev, data]);
      setActiveMilestoneId(data.id);
      return { success: true, milestone: data };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'milestone' }) };
    }
  }, [boqProjectId, milestones]);

  // Snapshot the live bill into a revision under a milestone.
  const snapshot = useCallback(async (milestoneId, label, isAuto = false) => {
    try {
      const { error: rpcErr } = await supabase.schema('boq')
        .rpc('snapshot_bill_version', { p_milestone_id: milestoneId, p_label: label || null, p_is_auto: isAuto });
      if (rpcErr) throw rpcErr;
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useBoqBillVersions] snapshot failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'snapshot', entity: 'version' }) };
    }
  }, [fetchAll]);

  // Create a new milestone and snapshot the live bill as its first revision.
  const newMilestoneWithSnapshot = useCallback(async ({ name, kind, label }) => {
    const m = await createMilestone({ name, kind });
    if (!m.success) return m;
    return snapshot(m.milestone.id, label || null, false);
  }, [createMilestone, snapshot]);

  // Restore a version into the live bill, auto-snapshotting current first so
  // nothing is lost.
  const restore = useCallback(async (versionId) => {
    try {
      const safetyMs = activeMilestone || latestMilestone;
      if (safetyMs) {
        await supabase.schema('boq').rpc('snapshot_bill_version', {
          p_milestone_id: safetyMs.id, p_label: 'Auto — before restore', p_is_auto: true,
        });
      }
      const { error: rpcErr } = await supabase.schema('boq').rpc('restore_bill_version', { p_version_id: versionId });
      if (rpcErr) throw rpcErr;
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useBoqBillVersions] restore failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'restore', entity: 'version' }) };
    }
  }, [activeMilestone, latestMilestone, fetchAll]);

  // Switch the live bill to a different stage. The SQL function snapshots the
  // stage being left, restores the target stage's latest revision, and
  // repoints active_milestone_id — all atomically.
  const switchStage = useCallback(async (targetMilestoneId) => {
    try {
      const { error: rpcErr } = await supabase.schema('boq')
        .rpc('switch_stage', { p_target_milestone_id: targetMilestoneId });
      if (rpcErr) throw rpcErr;
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useBoqBillVersions] switchStage failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'switch', entity: 'stage' }) };
    }
  }, [fetchAll]);

  const deleteVersion = useCallback(async (versionId) => {
    try {
      const { error: delErr } = await supabase.schema('boq').from('bill_versions').delete().eq('id', versionId);
      if (delErr) throw delErr;
      setVersions((prev) => prev.filter((v) => v.id !== versionId));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'version' }) };
    }
  }, []);

  // Load a version's frozen lines (for view / compare / PDF).
  const fetchVersionLines = useCallback(async (versionId) => {
    const { data, error: fErr } = await supabase.schema('boq').from('bill_version_lines')
      .select('item_version_id, full_code, description, unit, trade, line_type, project_zone_id, zone_label, quantity, measure_basis, rate_quoted, rate_billed, amount_quoted, amount_billed, sequence_order, option_group, is_selected')
      .eq('version_id', versionId)
      .order('sequence_order');
    if (fErr) throw fErr;
    return (data || []).map((l) => ({
      itemVersionId: l.item_version_id,
      fullCode: l.full_code,
      description: l.description,
      unit: l.unit,
      trade: l.trade,
      lineType: l.line_type || 'measured',
      projectZoneId: l.project_zone_id,
      zoneLabel: l.zone_label,
      quantity: num(l.quantity),
      measureBasis: l.measure_basis,
      rateQuoted: num(l.rate_quoted),
      rateBilled: num(l.rate_billed),
      amountQuoted: num(l.amount_quoted),
      amountBilled: num(l.amount_billed),
      sequenceOrder: l.sequence_order,
      optionGroup: l.option_group || null,
      isSelected: l.is_selected !== false,
      // shape compatible with renderBillHtml (version: {fullCode, shortDescription, unit})
      version: { fullCode: l.full_code, shortDescription: l.description, unit: l.unit },
      descriptionOverride: null,
    }));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return {
    milestones, versions, loading, error, latestMilestone,
    activeMilestoneId, activeMilestone,
    refresh: fetchAll, createMilestone, snapshot, newMilestoneWithSnapshot, restore, switchStage, deleteVersion, fetchVersionLines,
  };
};
